import classNames from 'classnames';
import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  CollectionPoint,
  CollectionPointSearchParams,
  CollectionPointWithI18nKey,
} from '../../../../types/collectionPoints';
import { joinZipCodeCity } from '../../../../utils/address';
import callApi from '../../../../utils/callApi';
import { DeliveryOption, useCheckoutState } from '../../../../utils/checkoutState';
import { config } from '../../../../utils/config';
import { geocode } from '../../../../utils/geocode';
import { reportErrorToSentry } from '../../../../utils/initializeSentry';
import { pushTrackingEvent } from '../../../../utils/tracking';
import LoadingIndicator from '../../../atoms/LoadingIndicator';
import { showDefaultError, showNotification } from '../../Notifications';
import AutocompleteSearchSection from './AutocompleteSearchSection';
import Dropdown from './Dropdown';
import Filters from './Filters';
import getCollectionPointI18nKey from './getCollectionPointI18nKey';
import List from './List';
import Map from './Map';
import SearchSection from './SearchSection';
import { getSearchParamsFromAddress, getSearchParamsFromCurrentPosition } from './searchUtils';
import ViewOptions, { ViewOption } from './ViewOptions';

const getCollectionPointTypes = (availableDeliveryOptions: DeliveryOption[] | undefined, isStorePickup: boolean) =>
  (availableDeliveryOptions ?? [])
    .map(({ collectionPointType }) => collectionPointType)
    .filter(collectionPointType =>
      isStorePickup ? collectionPointType === 'generic' : collectionPointType !== 'generic'
    );

const statusCodeI18nKeys = {
  404: 'shipping.notifications.noCollectionPointsFound',
  422: 'shipping.notifications.validationError',
};

type SearchPageProps = {
  searchParams: CollectionPointSearchParams;
  setSearchParams: (params: CollectionPointSearchParams) => void;
  results?: CollectionPointWithI18nKey[];
  setResults: (locations: CollectionPointWithI18nKey[]) => void;
  onSelect: (id: string) => void;
  view: ViewOption;
  setView: (view: ViewOption) => void;
  isStorePickup: boolean;
  isGoogleMapsSdkLoaded: boolean;
};

const SearchPage = ({
  searchParams,
  setSearchParams,
  results,
  setResults,
  onSelect,
  view,
  setView,
  isStorePickup,
  isGoogleMapsSdkLoaded,
}: SearchPageProps) => {
  const [isEditing, setEditing] = useState(!searchParams.streetHouseNumber);
  const [isLoading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const { checkoutState } = useCheckoutState();
  const { formatMessage } = useIntl();
  const isMapView = view === 'map';

  const availableFilters =
    results &&
    [...new Set(results.map(result => result.deliveryOption.collectionPointType))].map(collectionPointType => ({
      i18nKey: results.find(result => result.deliveryOption.collectionPointType === collectionPointType)!.i18nKey,
      collectionPointType: collectionPointType!,
    }));

  const filteredResults = useMemo(
    () => results?.filter(location => location.deliveryOption.collectionPointType === filter),
    [results, filter]
  );
  const resultsToRender = filter ? filteredResults : results;

  const onSearch = async (_searchParams: CollectionPointSearchParams) => {
    setLoading(true);
    setSearchParams(_searchParams);
    const { streetHouseNumber, zipCode, city, maximumDistance } = _searchParams;
    const availableDeliveryOptions = checkoutState.orderOptions.shipping?.collectionPointDelivery;
    const cityZipCode = joinZipCodeCity({ zipCode, city, country: { iso2Code: config.country } });
    try {
      const coordinates = searchParams.coordinates ?? (await geocode([streetHouseNumber, cityZipCode].join(', ')));
      const collectionPointSearchResponse = await callApi({
        endpoint: '/collection-points',
        urlParams: {
          streetHouseNumber,
          zipCode,
          city,
          latitude: coordinates.latitude.toString(),
          longitude: coordinates.longitude.toString(),
          maximumDistance: maximumDistance.toString(),
          maximumResults: '100',
          // TODO Use BE for all "type" filtering (currently only used for collection point vs. store pickup)
          type: getCollectionPointTypes(availableDeliveryOptions, isStorePickup).join(','),
        },
      });
      setResults(
        collectionPointSearchResponse.items.map((location: CollectionPoint) => ({
          ...location,
          i18nKey: getCollectionPointI18nKey(location),
        }))
      );
      setEditing(false);
      setLoading(false);
    } catch (e: any) {
      setLoading(false);
      pushTrackingEvent({
        event: 'error',
        action: 'collection_points',
        label: `${config.country}|${e.name}|${e.message}`,
        value: null,
      });
      const errorI18nKey = statusCodeI18nKeys[e.statusCode];
      if (errorI18nKey) {
        showNotification({
          level: 'warning',
          text: formatMessage({ id: errorI18nKey }),
          autoClose: true,
        });
        return;
      }
      console.error(e);
      showDefaultError();
      reportErrorToSentry(e);
    }
  };

  const filters = !!availableFilters?.length && (
    <Filters availableFilters={availableFilters} activeFilter={filter} onClick={setFilter} />
  );

  const existingShippingAddress = checkoutState.orderState.addresses?.shipping;

  const dropdown = (
    <Dropdown
      lastUsedAddress={existingShippingAddress}
      onMyLocation={async () => {
        setLoading(true);
        const _searchParams = await getSearchParamsFromCurrentPosition();
        if (!_searchParams) {
          setLoading(false);
          return;
        }
        onSearch(_searchParams);
      }}
      onLastUsedAddress={() => onSearch(getSearchParamsFromAddress(existingShippingAddress!))}
    />
  );

  return (
    <>
      <div className={classNames({ 'collection-point-search-page__search-wrapper': isMapView })}>
        {config.collectionPoints.googleMapsAutocomplete.enabled ? (
          <AutocompleteSearchSection
            onSearch={onSearch}
            initialValues={searchParams}
            isLoading={isLoading}
            isGoogleMapsSdkLoaded={isGoogleMapsSdkLoaded}
            filters={filters}
            dropdown={dropdown}
          />
        ) : (
          <SearchSection
            onSearch={onSearch}
            onSearchEdit={() => setEditing(true)}
            isEditing={isEditing}
            initialValues={searchParams}
            isLoading={isLoading}
            filters={filters}
            dropdown={dropdown}
          />
        )}
      </div>
      {isGoogleMapsSdkLoaded ? (
        <Map locations={resultsToRender} onMarkerClick={onSelect} />
      ) : (
        <div className="collection-point-search-page__map">
          <LoadingIndicator />
        </div>
      )}
      <List locations={resultsToRender} onItemClick={onSelect} />
      <ViewOptions value={view} onChange={setView} />
    </>
  );
};
export default SearchPage;
